import React from 'react';
import { Descriptions, Modal, Typography } from 'antd';
import { FileImageOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined } from '@ant-design/icons';
import {
  formatMaterialSize,
  getMaterialScopeLabel,
  getMaterialSourceLabel,
  getMaterialStatusLabel,
  inferSourceKind,
} from '../presentation/sourceMaterialView.js';

const { Paragraph, Text } = Typography;
const DETAIL_ICONS = new Map([
  ['word', <FileWordOutlined />],
  ['pdf', <FilePdfOutlined />],
  ['image', <FileImageOutlined />],
]);

function normalizeList(value) {
  return (Array.isArray(value) ? value : [])
    .map((item) => String(item || '').trim())
    .filter(Boolean);
}

function normalizeObject(value) {
  return (
    value
    && typeof value === 'object'
    && !Array.isArray(value)
  ) ? value : {};
}

function firstTruthy(...values) {
  return values.find(Boolean);
}

function formatTimestamp(value) {
  if (!value) return '-';
  const timestamp = new Date(value);
  return Number.isNaN(timestamp.getTime()) ? '-' : timestamp.toLocaleString();
}

function DetailList({ items }) {
  return (
    <ul className="flare-source-detail-list">
      {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
  );
}

function SourceUnderstanding({ summary, keyFacts, openQuestions, missingFields }) {
  const hasUnderstanding = [summary, keyFacts.length, openQuestions.length, missingFields.length].some(Boolean);
  if (!hasUnderstanding) {
    return <Text type="secondary">暂无可见提炼结果。</Text>;
  }
  return (
    <>
      {summary ? <Paragraph>{summary}</Paragraph> : null}
      {keyFacts.length > 0 ? (
        <section>
          <Text type="secondary">关键信息</Text>
          <DetailList items={keyFacts} />
        </section>
      ) : null}
      {openQuestions.length > 0 ? (
        <section>
          <Text type="secondary">待确认</Text>
          <DetailList items={openQuestions} />
        </section>
      ) : null}
      {missingFields.length > 0 ? (
        <section>
          <Text type="secondary">缺失字段</Text>
          <DetailList items={missingFields} />
        </section>
      ) : null}
    </>
  );
}

export default function SourceDetailModal({
  source,
  onClose,
}) {
  const sourceRecord = normalizeObject(source);
  const profile = normalizeObject(sourceRecord.document_profile);
  const understanding = normalizeObject(sourceRecord.document_understanding);
  const keyFacts = normalizeList(understanding.key_facts);
  const openQuestions = normalizeList(understanding.open_questions);
  const missingFields = normalizeList(understanding.missing_fields);
  const displayName = String(firstTruthy(profile.display_name, sourceRecord.filename, sourceRecord.name, '-'));
  const contextLabel = String(firstTruthy(profile.context_label, profile.file_kind, '-'));
  const sourceKind = inferSourceKind(displayName, firstTruthy(sourceRecord.mime_type, sourceRecord.type));
  const summary = String(understanding.summary ?? '').trim();
  const detailItems = [
    { key: 'profile', label: '资料档案', children: contextLabel },
    { key: 'status', label: '状态', children: source ? getMaterialStatusLabel(sourceRecord.status) : '-' },
    { key: 'size', label: '大小', children: formatMaterialSize(Number(sourceRecord.size)) },
    { key: 'source', label: '来源', children: getMaterialSourceLabel(sourceRecord.source) },
    { key: 'scope', label: '作用域', children: getMaterialScopeLabel(sourceRecord.scope) },
    { key: 'created', label: '上传时间', children: formatTimestamp(sourceRecord.created_at) },
  ];

  return (
    <Modal
      cancelButtonProps={{ style: { display: 'none' } }}
      className="flare-source-detail-modal"
      destroyOnHidden={false}
      maskTransitionName=""
      okText="确定"
      onCancel={onClose}
      onOk={onClose}
      open={Boolean(source)}
      title={(
        <span className="flare-source-detail-title">
          <span aria-hidden="true">{DETAIL_ICONS.get(sourceKind) ?? <FileOutlined />}</span>
          <span>{displayName}</span>
        </span>
      )}
      transitionName=""
      width={680}
    >
      <Descriptions
        className="flare-source-detail-metadata"
        column={{ xs: 1, sm: 2 }}
        items={detailItems}
        layout="vertical"
        size="small"
      />
      <div className="flare-source-detail-understanding">
        <Text strong>资料提炼</Text>
        <SourceUnderstanding
          keyFacts={keyFacts}
          missingFields={missingFields}
          openQuestions={openQuestions}
          summary={summary}
        />
      </div>
    </Modal>
  );
}
